import React, { useEffect, useState } from 'react'
import { fmt } from '../api.js'
import { portal } from '../portalApi.js'
import NoteDrawer from '../components/NoteDrawer.jsx'
import { CompanyPicker, NeedCompany, costGet, useCompany, useMeta, useToast } from './CostCommon.jsx'

// 원가관리 · 상제품 충당금.
// 기말 수량을 최신 입고분부터 덮어 나가고, 덮이지 않은 잔량이 오래 묵은 재고다(선입선출).
// 입고 이력으로 설명이 안 되는 몫은 '빈티지 미상'으로 따로 세운다 — 모르는 걸 늙었다고
// 단정하면 충당금이 부풀고, 젊다고 하면 과소가 된다. 어느 쪽도 추측할 일이 아니다.

export default function CostAllowance({ month, onMonthChange, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [company, setCompany] = useCompany()
  const [meta] = useMeta()
  const [data, setData] = useState(null)
  const [rates, setRates] = useState(null)   // {bucket: '0.5'} 편집 중 값
  const [runs, setRuns] = useState([])
  const [filter, setFilter] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [notify, toast] = useToast()

  const load = (overrideRates) => {
    setErr('')
    const params = { month, company }
    for (const [k, v] of Object.entries(overrideRates || {})) params[`rate_${k}`] = v
    costGet('/allowance', params).then((d) => {
      setData(d)
      if (!overrideRates) setRates(null)
    }).catch((e) => setErr(e.message))
    costGet('/allowance/runs', { company }).then(setRuns).catch(() => setRuns([]))
  }
  useEffect(() => load(), [month, company])

  const effRates = () => {
    const base = { ...(data?.rates || {}) }
    for (const [k, v] of Object.entries(rates || {})) base[k] = Number(v) || 0
    return base
  }

  const apply = () => load(effRates())

  const saveRates = async () => {
    try {
      await portal.post('/api/cost/allowance/rates', { company, rates: effRates() })
      notify('설정률 저장')
      load()
    } catch (e) { setErr(e.message) }
  }

  const confirm = async () => {
    setBusy(true)
    try {
      const res = await portal.post('/api/cost/allowance/confirm',
        { company, period: month, rates: effRates() })
      notify(`${month} 확정 — 설정액 ${fmt(res.allowance)}원 / 전입 ${fmt(res.delta)}원`, 5000)
      load()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  if (err && !data) return <div className="error-box">{err}</div>
  if (!data) return <div className="muted">불러오는 중…</div>
  const t = data.totals || {}
  const buckets = data.buckets || []
  const unknownShare = t.closingAmt ? (t.unknown || 0) / t.closingAmt : 0

  const q = filter.trim().toLowerCase()
  const rows = (data.rows || [])
    .filter((r) => r.allowance > 0 || (r.qty && (r.qty.unknown > 0 || r.qty.defect > 0)))
    .filter((r) => !q || [r.sku, r.name, r.option_name].some((v) => (v || '').toLowerCase().includes(q)))
    .sort((a, b) => b.allowance - a.allowance || b.amt.unknown - a.amt.unknown)

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>{month} 상제품 충당금 설정</h2>
        <NoteDrawer target="allowance" screenKey="cost_allowance"
          context={{ month }} contextLabel={month}
          onNavigate={(ctx) => ctx?.month && onMonthChange?.(ctx.month)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      <div className="row" style={{ marginBottom: 12 }}>
        <CompanyPicker value={company} onChange={setCompany} meta={meta} />
      </div>
      {err && <div className="error-box">{err}</div>}
      {!company && <NeedCompany>통합 화면에서는 합산 결과만 봅니다. 설정률 저장과 확정은 사업부를 골라야 합니다.</NeedCompany>}

      <div className="hint">
        기말 수량을 <b>최신 입고분부터</b> 덮어 나가고, 덮이지 않은 잔량을 오래된 층으로 밉니다(선입선출).
        불량 재고는 연령과 무관하게 따로 뺍니다.
        <b> 빈티지 미상</b>은 입고 이력이 모자라 연령을 정하지 못한 몫입니다 — 전기 입출고를 올리면 줄어듭니다.
      </div>

      <div className="cards">
        <div className="card"><div className="label">기말 재고금액</div><div className="value">{fmt(t.closingAmt)}원</div></div>
        <div className="card"><div className="label">이번 설정액</div><div className="value">{fmt(t.allowance)}원</div></div>
        <div className="card"><div className="label">직전 설정액</div><div className="value">{fmt(data.prior)}원</div></div>
        <div className="card"><div className="label">당기 전입액</div><div className="value">{fmt(data.delta)}원</div></div>
      </div>

      {unknownShare > 0.01 && (
        <div className="error-box">
          빈티지 미상이 기말 재고금액의 {(unknownShare * 100).toFixed(1)}% ({fmt(t.unknown)}원)입니다.
          이 몫은 연령을 알 수 없어 기본 설정률 0으로 두었습니다 — 그대로 확정하면 충당금이 과소일 수 있습니다.
          전기 입고 이력을 올리거나, 아래에서 미상 설정률을 직접 정하세요.
        </div>
      )}

      <div className="panel">
        <h3>연령 구간별</h3>
        <table style={{ maxWidth: 900 }}>
          <thead>
            <tr><th className="l">구간</th><th>수량</th><th>재고금액</th><th>구성비</th><th>설정률</th><th>충당금</th></tr>
          </thead>
          <tbody>
            {buckets.map((b) => {
              const amt = t[b.key] || 0
              const rate = rates?.[b.key] ?? String(data.rates?.[b.key] ?? 0)
              return (
                <tr key={b.key}>
                  <td className="l">{b.label}{b.key === 'unknown' && <span className="warn"> ⚠</span>}</td>
                  <td>{fmt(t[b.key + 'Qty'])}</td>
                  <td>{fmt(amt)}</td>
                  <td>{t.closingAmt ? ((amt / t.closingAmt) * 100).toFixed(1) : '0.0'}%</td>
                  <td>
                    <input style={{ width: 70 }} value={rate}
                      onChange={(e) => setRates({ ...rates, [b.key]: e.target.value })} />
                  </td>
                  <td>{fmt(amt * (Number(rate) || 0))}</td>
                </tr>
              )
            })}
            <tr className="total">
              <td className="l">합계</td>
              <td /><td>{fmt(t.closingAmt)}</td><td>100%</td><td />
              <td>{fmt(t.allowance)}</td>
            </tr>
          </tbody>
        </table>
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn secondary" onClick={apply} disabled={!rates}>설정률 적용해 다시 계산</button>
          <button className="btn secondary" onClick={saveRates} disabled={!company}>설정률 저장</button>
          <button className="btn" onClick={confirm} disabled={!company || busy}>
            {busy ? '확정 중…' : `${month} 확정`}
          </button>
        </div>
        <div className="hint">
          설정률은 비율입니다 (1 = 100%). 확정하면 스냅샷이 남고, 다음 기 <b>전입액 = 그때 설정액 − 이번 설정액</b>으로 이어집니다.
        </div>
      </div>

      <div className="panel">
        <h3>품목별 (충당금·미상·불량이 있는 것만)</h3>
        <div className="row" style={{ marginBottom: 10 }}>
          <input placeholder="검색 (품번/상품명)" style={{ width: 260 }}
            value={filter} onChange={(e) => setFilter(e.target.value)} />
          <span className="muted">{rows.length}품목</span>
        </div>
        <table>
          <thead>
            <tr>
              {!company && <th className="l">사업부</th>}
              <th className="l">품번</th><th className="l">상품명</th>
              <th>기말수량</th><th>평균단가</th><th>기말금액</th>
              {buckets.map((b) => <th key={b.key}>{b.label}</th>)}
              <th>충당금</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={buckets.length + 7} className="c muted">충당금 대상이 없습니다</td></tr>}
            {rows.slice(0, 500).map((r) => (
              <tr key={r.item_id}>
                {!company && <td className="l">{r.company_name}</td>}
                <td className="l">{r.sku}</td>
                <td className="l">{r.name} {r.option_name && <span className="muted">{r.option_name}</span>}</td>
                <td>{fmt(r.closingQty)}</td>
                <td>{fmt(r.avgCost, 1)}</td>
                <td>{fmt(r.closingAmt)}</td>
                {buckets.map((b) => (
                  <td key={b.key} className={b.key === 'unknown' && r.amt[b.key] > 0 ? 'warn' : ''}>
                    {fmt(r.amt[b.key])}
                  </td>
                ))}
                <td><b>{fmt(r.allowance)}</b></td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 500 && <div className="hint">상위 500건만 표시 중 — 검색으로 좁혀주세요.</div>}
      </div>

      {runs.length > 0 && (
        <div className="panel">
          <h3>확정 이력</h3>
          <table style={{ maxWidth: 820 }}>
            <thead><tr><th>기준월</th>{!company && <th className="l">사업부</th>}<th>기말 재고금액</th><th>설정액</th><th>직전</th><th>전입액</th><th>미상금액</th></tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={`${r.company}|${r.period}`}>
                  <td className="c">{r.period}</td>
                  {!company && <td className="l">{r.company_name}</td>}
                  <td>{fmt(r.base_amt)}</td>
                  <td>{fmt(r.allowance)}</td>
                  <td>{fmt(r.prior)}</td>
                  <td>{fmt(r.delta)}</td>
                  <td className={r.unknown_amt ? 'warn' : ''}>{fmt(r.unknown_amt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {toast}
    </>
  )
}
